import React from 'react'
import { Button, Col, Row } from 'react-bootstrap'
import DropdownInput from '../../components/forms/fields/DropdownInput'
import DropdownSelect from '../../components/forms/fields/DropdownSelect'
import BillingResultsTable from './BillingResultsTable/BillingResultsTable'
import AlertBox from '../../components/molecules/AlertBox'
import text from '../../_constants/text'
import icons from '../../_constants/icons'
import useBillingGuideForm from '../../hooks/useBillingGuideForm'
import { PostcodeData, ResultsStatus } from '../../domain/models/pages/BillingGuideLookupPage/BillingGuideLookupModel'
import '../../components/wrappers/Loader/Loader.scss'

const BillingGuideLookupForm: React.FC = () => {
  const { procedures, postcode, clinic, results } = useBillingGuideForm()

  // Render billing results section depending on state
  const renderBillingResults = () => {
    switch (results.status) {
      case ResultsStatus.SEARCHING:
        return (
          <div className="loading-state d-flex flex-column align-items-center justify-content-center py-5">
            <div className="lds-ellipsis mb-3">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </div>
            <p className="mb-0">{text.LOADING_RESULTS}</p>
          </div>
        )
      case ResultsStatus.ERROR:
        return (
          <div className="error-state">
            <AlertBox type="error" message={results.error || text.BILLING_GUIDE_ERROR_MESSAGE} />
          </div>
        )
      case ResultsStatus.SUCCESS:
        return <BillingResultsTable results={results.results} sourceTimestamp={results.sourceTimestamp} />
      case ResultsStatus.NO_RESULTS:
        return (
          <div className="no-results-state">
            <AlertBox type="warning" message={text.BILLING_GUIDE_NO_RESULTS} />
          </div>
        )
    }
  }

  return (
    <>
      <div className="billing-lookup-filters bg-white rounded p-4">
        <Row className="no-gutters gx-2">
          <Col xs={12} lg={10} className="d-flex flex-column flex-lg-row gx-2">
            <div className="flex-fill me-lg-2 mb-3 mb-lg-0">
              <DropdownInput
                label={text.BILLING_GUIDE_POSTCODE_LABEL}
                callback={postcode.onChange}
                dropdownItems={postcode.suggestions}
                onItemSelect={postcode.onSelect}
                value={postcode.value}
                error={postcode.error !== null}
                errorMessage={postcode.error}
                itemLabel={(_source: PostcodeData) => `${_source.postcode} - ${_source.suburb}`}
                onCloseDropdown={postcode.onCloseDropdown}
              />
            </div>

            <div className="flex-fill me-lg-2 mb-3 mb-lg-0">
              <DropdownSelect
                inputName="clinic"
                label={text.BILLING_GUIDE_LOCATION_LABEL}
                value={clinic.selectedItem}
                options={clinic.items}
                error={clinic.error !== null}
                errorMessage={clinic.error}
                disabled={clinic.items.length === 0}
                onChangeCb={(_, value) => {
                  if (value) {
                    clinic.onSelect(value)
                  }
                }}
              />
            </div>

            <div className="flex-fill mb-3 mb-lg-0">
              <DropdownSelect
                inputName="procedure"
                label={text.BILLING_GUIDE_PROCEDURE_LABEL}
                value={procedures.selectedItem}
                options={procedures.items}
                onChangeCb={(_, value) => {
                  if (value) {
                    procedures.onSelect(value)
                  }
                }}
              />
            </div>
          </Col>

          <Col xs={12} lg={2} className="d-flex mt-2 mt-lg-4">
            <Button
              variant="primary"
              className="w-100 mb-0 d-flex align-items-center justify-content-center"
              onClick={results.onSearch}
              disabled={clinic.items.length === 0 || !clinic.selectedItem || results.status === ResultsStatus.SEARCHING}
            >
              <i data-testid="FontIcon" className="material-icons-outlined icon mr-2">
                {icons.SEARCH}
              </i>
              <span>{results.status === ResultsStatus.SEARCHING ? text.SEARCHING : text.SEARCH}</span>
            </Button>
          </Col>
        </Row>
      </div>

      {results.status !== ResultsStatus.IDLE && (
        <div className="billing-results-table bg-white rounded p-4 mb-4">{renderBillingResults()}</div>
      )}
    </>
  )
}

export default BillingGuideLookupForm
